/**
 * PDF Generation Utility
 * 
 * This module provides a utility function for generating PDF documents from project sections.
 * It uses jsPDF and jspdf-autotable to create formatted PDFs with tables and summaries.
 * 
 * Features:
 * - Project section tables
 * - Section totals
 * - Project summary
 * - Automatic pagination
 * - Formatted tables
 */

import { ProjectSection } from "@/types/project.types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generates a PDF Blob from project sections
 * @param {ProjectSection[]} sections - Array of project sections
 * @param {() => number} calculateTotal - Function to calculate the total cost
 * @returns {Blob} PDF document as a Blob
 * 
 * @example
 * const pdfBlob = generatePdfBlob(sections, calculateTotal);
 * downloadBlob(pdfBlob, 'project-summary.pdf');
 * 
 * The generated PDF includes:
 * - Project title and generation date
 * - Detailed section tables with items, prices, and quantities
 * - Section subtotals
 * - Project summary with section totals
 * - Final project total
 */
export const generatePdfBlob = (sections: ProjectSection[], calculateTotal: () => number): Blob => {
	const doc = new jsPDF();
	let yPos = 20;

	doc.setFontSize(20);
	doc.text('Project Calculator Summary', 105, yPos, { align: 'center' });
	yPos += 15;

	doc.setFontSize(10);
	doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, yPos, {
		align: 'center',
	});
	yPos += 15;

	sections.forEach((section) => {
		if (section.items.length > 0) {
			doc.setFontSize(14);
			doc.text(section.title, 14, yPos);
			yPos += 10;

			const tableData = section.items.map((item) => [
				item.name,
				`$${item.price.toFixed(2)}`,
				item.quantity.toString(),
				`$${(item.price * item.quantity).toFixed(2)}`,
			]);

			autoTable(doc, {
				head: [['Item', 'Price', 'Quantity', 'Subtotal']],
				body: tableData,
				startY: yPos,
				theme: 'grid',
				headStyles: { fillColor: [75, 75, 75] },
				foot: [
					[
						'',
						'',
						'Section Total:',
						`$${section.items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)}`,
					],
				],
				footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
			});

			yPos = (doc as any).lastAutoTable.finalY + 15;

			if (yPos > 270) {
				doc.addPage();
				yPos = 20;
			}
		}
	});

	doc.setFontSize(16);
	doc.text('Project Summary', 14, yPos);
	yPos += 10;

	const summaryData = sections.map((section) => [
		section.title,
		`$${section.items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)}`,
	]);

	autoTable(doc, {
		head: [['Section', 'Total']],
		body: summaryData,
		startY: yPos,
		theme: 'grid',
		headStyles: { fillColor: [75, 75, 75] },
		foot: [['Project Total', `$${calculateTotal().toFixed(2)}`]],
		footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
	});

	return doc.output('blob');
};
